"use client";

import React, { useEffect, useState } from "react";

interface LegalContent {
    terms: string;
    privacy: string;
}

type LegalType = "terms" | "privacy";

interface LegalPageProps {
    type: LegalType;
}

const TITLES: Record<LegalType, string> = {
    terms: "Termos de Uso",
    privacy: "Política de Privacidade",
};

const logoIcon = "/assets/images/logo_icon_new.png";
const ctaLink = "https://app.sapieduca.com.br/ia";

/** Converts inline markdown (bold, italic) to React nodes */
function renderInline(text: string): React.ReactNode {
    const parts: React.ReactNode[] = [];
    const regex = /(\*\*(.+?)\*\*)|(\*(.+?)\*)|(_(.+?)_)/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let key = 0;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        if (match[1]) {
            parts.push(<strong key={key++} className="font-semibold text-slate-900">{match[2]}</strong>);
        } else if (match[3] || match[5]) {
            parts.push(<em key={key++}>{match[4] || match[6]}</em>);
        }
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }
    return parts.length === 1 ? parts[0] : parts;
}

/** Converts a markdown string to styled React elements */
function renderMarkdown(markdown: string): React.ReactNode[] {
    const lines = markdown.split("\n");
    const elements: React.ReactNode[] = [];
    let i = 0;
    let key = 0;

    while (i < lines.length) {
        const line = lines[i];

        // H1
        if (/^# /.test(line)) {
            elements.push(
                <h2 key={key++} className="text-2xl font-black text-slate-900 mt-10 mb-3 tracking-tight">
                    {renderInline(line.slice(2))}
                </h2>
            );
            i++;
            continue;
        }

        // H2
        if (/^## /.test(line)) {
            elements.push(
                <h3 key={key++} className="text-xl font-bold text-[#3266cc] mt-8 mb-2">
                    {renderInline(line.slice(3))}
                </h3>
            );
            i++;
            continue;
        }

        // H3
        if (/^### /.test(line)) {
            elements.push(
                <h4 key={key++} className="text-base font-bold text-slate-800 mt-6 mb-1">
                    {renderInline(line.slice(4))}
                </h4>
            );
            i++;
            continue;
        }

        // Horizontal rule
        if (/^---+$/.test(line.trim())) {
            elements.push(<hr key={key++} className="border-slate-200 my-6" />);
            i++;
            continue;
        }

        // Bullet list — collect consecutive items
        if (/^[-*] /.test(line)) {
            const items: React.ReactNode[] = [];
            while (i < lines.length && /^[-*] /.test(lines[i])) {
                items.push(
                    <li key={key++} className="ml-4 text-slate-700 leading-relaxed">
                        {renderInline(lines[i].slice(2))}
                    </li>
                );
                i++;
            }
            elements.push(
                <ul key={key++} className="list-disc list-inside space-y-1 my-4 pl-2">
                    {items}
                </ul>
            );
            continue;
        }

        // Empty line → skip
        if (line.trim() === "") {
            i++;
            continue;
        }

        // Regular paragraph
        elements.push(
            <p key={key++} className="text-slate-700 leading-relaxed my-3">
                {renderInline(line)}
            </p>
        );
        i++;
    }

    return elements;
}


export default function LegalPage({ type }: LegalPageProps) {
    const [content, setContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetch("https://api.sapieduca.com.br/legal", {
            method: "GET",
            headers: { "Content-Type": "application/json" },
            mode: "cors",
        })
            .then((res) => {
                if (!res.ok) throw new Error("Falha ao carregar conteúdo.");
                return res.json() as Promise<LegalContent>;
            })
            .then((data) => {
                setContent(data[type]);
                setLoading(false);
            })
            .catch(() => {
                setError("Não foi possível carregar o conteúdo. Tente novamente mais tarde.");
                setLoading(false);
            });
    }, [type]);

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">

            {/* Header - same as LandingPage */}
            <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-lg border-b border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20 relative">
                        <div className="flex-shrink-0 flex items-center gap-3">
                            <a href="/lp" className="flex items-center">
                                <div className="w-12 h-12 flex items-center justify-center">
                                    <img
                                        src={logoIcon}
                                        alt="Sapieduca Logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                            </a>
                        </div>
                        <div className="hidden md:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
                            <a href="/lp#RAG" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Sobre</a>
                            <a href="/lp#Mission" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Impacto</a>
                            <a href="/lp#Pricing" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Planos</a>
                            <a href="/lp#FAQ" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">FAQ</a>
                            <a href="/lp#Impact" className="text-sm font-semibold text-slate-600 hover:text-[#3266cc] transition-colors no-underline">Para Escolas</a>
                        </div>
                        <div>
                            <button
                                onClick={() => { window.location.href = ctaLink; }}
                                className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent text-sm font-bold rounded-full text-white bg-[#3266cc] hover:bg-[#2855b0] transition-all shadow-md hover:shadow-lg active:scale-95"
                            >
                                Entrar
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="py-16 bg-slate-50 min-h-[calc(100vh-160px)]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <a
                        href="/lp"
                        className="inline-flex items-center gap-1 text-[#3266cc] text-sm font-semibold mb-8 no-underline hover:underline"
                    >
                        ← Voltar
                    </a>
                    <h1 className="text-4xl font-black text-slate-900 mb-8 tracking-tight">
                        {TITLES[type]}
                    </h1>
                    <div className="w-16 h-1 bg-[#3266cc] rounded-full mb-10"></div>

                    {loading && (
                        <p className="text-slate-500 text-lg animate-pulse">Carregando...</p>
                    )}
                    {error && (
                        <p className="text-red-500 text-base">{error}</p>
                    )}
                    {!loading && !error && (
                        <div className="max-w-none">
                            {renderMarkdown(content)}
                        </div>
                    )}
                </div>
            </main>

            {/* Footer - same as LandingPage */}
            <footer className="bg-white border-t border-slate-100 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-start gap-8">
                    <div className="flex flex-col items-start">
                        <span className="font-extrabold text-3xl text-[#3266cc] tracking-tight whitespace-nowrap">Sapieduca IA</span>
                        <div className="flex flex-col gap-1 mt-2">
                            <a href="/termos" className="text-slate-400 text-sm hover:text-[#3266cc] transition-colors no-underline">Termos de Uso</a>
                            <a href="/privacidade" className="text-slate-400 text-sm hover:text-[#3266cc] transition-colors no-underline">Política de Privacidade</a>
                        </div>
                    </div>
                    <div className="text-slate-400 text-sm text-right font-medium">
                        <p className="mb-1">&copy; 2026 Sapientia Educação</p>
                        <p>Feito com carinho e propósito para todos os educadores do Brasil. 🇧🇷</p>
                        <p><a href="mailto:contato@sapieduca.com.br" className="text-slate-400 hover:text-[#3266cc] transition-colors no-underline">contato@sapieduca.com.br</a></p>
                    </div>
                </div>
            </footer>

        </div>
    );
}
